import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, RefreshCw, ArrowLeft, ShieldCheck, MessageSquareQuote, LogOut } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ADMIN_KEY_STORAGE = "ep_admin_key";

/**
 * Admin page for reviewing community testimonials. Prompts for the admin
 * key on first visit, stashes it in localStorage after successful load, and
 * shows pending whispers with one-tap approve / reject buttons.
 */
export default function AdminTestimonialsPage() {
  const [adminKey, setAdminKey] = useState(() => {
    try { return localStorage.getItem(ADMIN_KEY_STORAGE) || ""; } catch { return ""; }
  });
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [inputKey, setInputKey] = useState("");

  const load = async (key) => {
    setLoading(true);
    setError(null);
    try {
      const [pendingRes, approvedRes] = await Promise.all([
        fetch(`${BACKEND}/api/testimonials/pending?admin_key=${encodeURIComponent(key)}`),
        fetch(`${BACKEND}/api/testimonials?limit=60`),
      ]);
      if (pendingRes.status === 403) {
        try { localStorage.removeItem(ADMIN_KEY_STORAGE); } catch {}
        setAdminKey("");
        setError("Invalid admin key.");
        return;
      }
      if (!pendingRes.ok) throw new Error("failed to load pending");
      const p = await pendingRes.json();
      const a = approvedRes.ok ? await approvedRes.json() : { items: [] };
      setPending(p.items || []);
      setApproved(a.items || []);
    } catch (e) {
      console.error("[admin testimonials] load failed:", e);
      setError("Something went wrong loading testimonials.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (adminKey) load(adminKey); }, [adminKey]);

  const submitKey = (e) => {
    e.preventDefault();
    if (!inputKey.trim()) return;
    try { localStorage.setItem(ADMIN_KEY_STORAGE, inputKey.trim()); } catch {}
    setAdminKey(inputKey.trim());
  };

  const signOut = () => {
    try { localStorage.removeItem(ADMIN_KEY_STORAGE); } catch {}
    setAdminKey("");
    setInputKey("");
    setPending([]);
    setApproved([]);
  };

  const act = async (id, action) => {
    try {
      const res = await fetch(
        `${BACKEND}/api/testimonials/${id}/${action}?admin_key=${encodeURIComponent(adminKey)}`,
        { method: "POST" }
      );
      if (!res.ok) throw new Error("failed");
      toast.success(action === "approve" ? "Approved — it'll rotate through soon." : "Rejected.");
      // Optimistic update
      setPending((prev) => prev.filter((t) => t.id !== id));
      if (action === "approve") load(adminKey);
    } catch (e) {
      console.error(`[admin testimonials] ${action} failed for ${id}:`, e);
      toast.error("Couldn't complete the action — try again.");
    }
  };

  // ── Gate: prompt for admin key if we don't have one ──
  if (!adminKey) {
    return (
      <div className="min-h-screen relative z-10 flex items-center justify-center px-6">
        <form
          onSubmit={submitKey}
          className="w-full max-w-md rounded-2xl border border-[color:var(--gold)]/25 p-8 md:p-10"
          style={{ background: "linear-gradient(135deg, rgba(212,162,76,0.06), rgba(229,168,124,0.02))" }}
          data-testid="admin-key-form"
        >
          <ShieldCheck size={22} className="text-[color:var(--gold)] mx-auto" />
          <h1 className="mt-4 font-serif-display text-[color:var(--parchment)] text-2xl md:text-3xl text-center">
            Admin · Testimonials
          </h1>
          <p className="mt-3 font-serif-italic text-[color:var(--parchment-soft)] text-center text-sm">
            Enter the admin key to review pending whispers.
          </p>
          <Input
            type="password"
            value={inputKey}
            onChange={(e) => setInputKey(e.target.value)}
            placeholder="Admin key"
            required
            className="mt-6 bg-white border-slate-300 rounded-lg h-12 text-slate-900 font-sans-body"
            data-testid="admin-key-input"
          />
          <Button
            type="submit"
            className="mt-4 w-full h-12 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium"
            data-testid="admin-key-submit"
          >
            Sign in
          </Button>
          {error && (
            <p className="mt-4 text-center text-[color:var(--accent-warning)] font-serif-italic text-sm" data-testid="admin-key-error">
              {error}
            </p>
          )}
          <Link
            to="/"
            className="mt-6 block text-center label-overline text-[10px] text-[color:var(--text-secondary)] hover:text-[color:var(--gold)]"
          >
            ← Back to Earth Pulse
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative z-10">
      <header className="sticky top-0 z-30 backdrop-blur-md border-b border-[color:var(--gold)]/12" style={{ background: "rgba(5,9,23,0.72)" }}>
        <div className="mx-auto max-w-[1100px] px-6 md:px-10 py-4 md:py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 hover:opacity-90" data-testid="admin-back">
            <ArrowLeft size={14} className="text-[color:var(--gold)]" />
            <span className="label-overline">Back</span>
          </Link>
          <div className="label-overline">Admin · Testimonials</div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => load(adminKey)}
              disabled={loading}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[color:var(--gold)]/40 text-[color:var(--gold)] label-overline text-[10px] hover:bg-[color:var(--gold)]/10 disabled:opacity-50"
              data-testid="admin-refresh"
            >
              <RefreshCw size={11} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              type="button"
              onClick={signOut}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[color:var(--gold)]/30 text-[color:var(--text-secondary)] label-overline text-[10px] hover:bg-[color:var(--gold)]/10"
              data-testid="admin-signout"
            >
              <LogOut size={11} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1100px] px-6 md:px-10 py-12 md:py-16 space-y-14" data-testid="admin-page">
        {/* Pending column */}
        <section data-testid="admin-pending-section">
          <div className="flex items-center gap-3 mb-6">
            <MessageSquareQuote size={16} className="text-[color:var(--gold)]" />
            <h2 className="font-serif-display text-[color:var(--parchment)] text-2xl md:text-3xl">Pending</h2>
            <span className="label-overline text-[10px]">{pending.length} awaiting review</span>
          </div>
          {pending.length === 0 ? (
            <p className="font-serif-italic text-[color:var(--parchment-soft)]" data-testid="admin-pending-empty">
              No pending whispers right now. All caught up.
            </p>
          ) : (
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-pending-list">
              {pending.map((t) => (
                <li
                  key={t.id}
                  className="parchment-card rounded-2xl p-6"
                  data-testid={`admin-pending-${t.id}`}
                >
                  <div className="label-overline text-[10px]">
                    {t.name}{t.city ? ` · ${t.city}` : ""}
                    <span className="text-[color:var(--text-muted)] ml-2">
                      · {new Date(t.created_at).toLocaleString()}
                    </span>
                  </div>
                  <blockquote className="mt-3 font-serif-italic text-[color:var(--parchment)] text-base md:text-lg leading-relaxed">
                    &ldquo;{t.body}&rdquo;
                  </blockquote>
                  <div className="mt-5 flex gap-2">
                    <button
                      type="button"
                      onClick={() => act(t.id, "approve")}
                      data-testid={`admin-approve-${t.id}`}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium text-sm"
                    >
                      <Check size={13} /> Approve
                    </button>
                    <button
                      type="button"
                      onClick={() => act(t.id, "reject")}
                      data-testid={`admin-reject-${t.id}`}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[color:var(--gold)]/40 hover:bg-[color:var(--accent-warning)]/10 hover:border-[color:var(--accent-warning)]/40 text-[color:var(--parchment-soft)] font-medium text-sm"
                    >
                      <X size={13} /> Reject
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Approved column */}
        <section data-testid="admin-approved-section">
          <div className="flex items-center gap-3 mb-6">
            <Check size={16} className="text-[color:var(--gold)]" />
            <h2 className="font-serif-display text-[color:var(--parchment)] text-2xl md:text-3xl">Approved</h2>
            <span className="label-overline text-[10px]">
              {approved.length} rotating through the marquee
            </span>
          </div>
          {approved.length === 0 ? (
            <p className="font-serif-italic text-[color:var(--parchment-soft)]">
              None yet — approve pending whispers and they&apos;ll appear here.
            </p>
          ) : (
            <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {approved.map((t) => (
                <li key={t.id} className="parchment-card rounded-2xl p-5">
                  <blockquote className="font-serif-italic text-[color:var(--parchment)] text-sm md:text-base leading-snug">
                    &ldquo;{t.body}&rdquo;
                  </blockquote>
                  <div className="mt-3 label-overline text-[10px] text-[color:var(--gold)]/85">
                    — {t.name}{t.city ? `, ${t.city}` : ""}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}
